import { type JSX } from 'react';
import { useTheme } from '../../theme';
import { useIntl } from '@doxyme/locale';
import {
  VIEWBOX_SIZE,
  STROKE_WIDTH,
  RADIUS,
  CENTER,
  CIRCUMFERENCE,
  rotate,
  dash,
} from './spinnerPrimitives';

export type SpinnerMode = 'green' | 'white' | 'light' | 'dark' | 'darkGreen';

type CommonProps = {
  disableShrink?: boolean;
  size?: number;
  mode?: SpinnerMode;
  'aria-label'?: string;
  hasTrack?: boolean;
};

type DeterminateProps = {
  variant: 'determinate';
  value: number;
};

type IndeterminateProps = {
  variant?: 'indeterminate';
  value?: never;
};

export type SpinnerProps = CommonProps &
  (DeterminateProps | IndeterminateProps);

export const Spinner = ({
  disableShrink = false,
  size = 20,
  mode = 'dark',
  variant = 'indeterminate',
  value,
  'aria-label': ariaLabel,
  hasTrack = false,
}: SpinnerProps): JSX.Element => {
  const theme = useTheme();
  const { formatMessage } = useIntl();

  const trackColor = {
    green: theme.palette.brand.primary[25],
    white: theme.palette.neutral.lightAlpha[400],
    light: theme.palette.neutral.lightAlpha[250],
    dark: theme.palette.neutral.darkAlpha[550],
    darkGreen: theme.loader.background,
  }[mode];

  const indicatorColor = {
    green: theme.palette.brand.primary[100],
    white: theme.palette.neutral.lightAlpha[100],
    light: theme.palette.brand.secondary[100],
    dark: theme.palette.brand.secondary[100],
    darkGreen: theme.loader.status,
  }[mode];

  const label = ariaLabel ?? formatMessage({ id: 'loading' });

  const determinateOffset =
    variant === 'determinate'
      ? CIRCUMFERENCE - (CIRCUMFERENCE * (value ?? 0)) / 100
      : 0;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${VIEWBOX_SIZE} ${VIEWBOX_SIZE}`}
      role='progressbar'
      aria-label={label}
      {...(variant === 'determinate' && {
        'aria-valuenow': value,
        'aria-valuemin': 0,
        'aria-valuemax': 100,
      })}
      css={{
        ...(variant === 'indeterminate' && {
          animation: `${rotate} 1.4s linear infinite`,
        }),
      }}
    >
      {hasTrack && (
        <circle
          cx={CENTER}
          cy={CENTER}
          r={RADIUS}
          fill='none'
          stroke={trackColor}
          strokeWidth={STROKE_WIDTH}
          aria-hidden
        />
      )}

      {variant === 'determinate' && (
        <circle
          cx={CENTER}
          cy={CENTER}
          r={RADIUS}
          fill='none'
          stroke={indicatorColor}
          strokeWidth={STROKE_WIDTH}
          strokeLinecap='round'
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={determinateOffset}
          css={{
            transform: 'rotate(-90deg)',
            transformOrigin: '50% 50%',
            transition: 'stroke-dashoffset 0.3s ease',
          }}
        />
      )}

      {variant === 'indeterminate' && (
        <circle
          cx={CENTER}
          cy={CENTER}
          r={RADIUS}
          fill='none'
          stroke={indicatorColor}
          strokeWidth={STROKE_WIDTH}
          strokeLinecap='round'
          css={
            disableShrink
              ? {
                  strokeDasharray: CIRCUMFERENCE * 0.6,
                }
              : {
                  animation: `${dash} 1.4s ease-in-out infinite`,
                }
          }
        />
      )}
    </svg>
  );
};
